import dayjs from "dayjs";
import useTranslation from "next-translate/useTranslation";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useRouter } from "next/router";
import { Suspense, useEffect, useState } from "react";
import Swal from "sweetalert2";
import { useSession } from "next-auth/react";
import { ICON_SIZE, copyTextToClipboard, highlightLinksInText } from "../../helpers/application";
import { defaultToast } from "../../helpers/sweet_alert_helper";
import {
  deleteUserFromEvent,
  getEventChatRoom,
  getEventUsersForInvite,
  joinUserToEvent,
  approveUsersEvent,
  declineUsersEvent,
} from "../../src/queries";
import Navbar from "../navbars/navbar";
import BlockGap from "../shared/gap";
import ImageIcon from "../shared/image_icon";
import UserImage from "../shared/user_image";
import Badges from "./badges";
import orderBy from "lodash/orderBy";
import { goBack } from "../../helpers/application";
import { canAddMembers, pastEvent, inviteUserToEvent } from "../../helpers/event_helper";
import { push } from "@socialgouv/matomo-next";
import IconsList from "../groups/icons_list";
import NavbarBottom from "../navbars/navbar_bottom";
import { ThreeDots, XLg } from "react-bootstrap-icons";
import DummyMargin from "../shared/dummy_margin";
import AvatarList from "../groups/avatar_list";
const RatingSheet = dynamic(() => import("./rating_sheet"), { suspense: true });
const InviteSheet = dynamic(() => import("./invite_sheet"), { suspense: true });
const MenuSheet = dynamic(() => import("./menu_sheet"), { suspense: true });
const ApproveSheet = dynamic(() => import("./approve_sheet"), { suspense: true });
const MapSheet = dynamic(() => import("../events/map_sheet", { suspense: true }));

const Detail = (props) => {
  const { t } = useTranslation("common");
  const [approvedUsers, setApprovedUsers] = useState([]);
  const [waitingUsers, setWaitingUsers] = useState([]);
  const [currentUserEvent, setCurrentUserEvent] = useState(null);
  const [showMenuSheet, setShowMenuSheet] = useState(false);
  const [showRateSheet, setShowRateSheet] = useState(false);
  const [showInviteSheet, setShowInviteSheet] = useState(false);
  const [usersEvents, setUsersEvents] = useState([]);
  const [usersEvent, setUsersEvent] = useState(null);
  const [showMapSheet, setShowMapSheet] = useState(false);
  const Toast = defaultToast;
  const router = useRouter();
  const membersLink = `/events/${props.event.slug}/members`;
  const { data: session } = useSession();

  const eventURL = `${process.env.NEXT_PUBLIC_APP_URL_LOCAL}${router.asPath}`;

  const copyAddress = () => {
    const address = currentUserEvent?.status === "approved" ? props.event.full_address : props.event.city;
    copyTextToClipboard(window, address);

    Toast.fire({
      icon: "success",
      title: "Adrress copied",
    });
  };

  useEffect(() => {
    const sorted = orderBy(props.event.users_events, ["is_admin", "id"], "desc");

    setApprovedUsers(sorted.filter((ue) => ue.status === "approved"));
    setWaitingUsers(sorted.filter((ue) => ue.status === "waiting_approval"));
    setCurrentUserEvent(sorted.find((ue) => ue.user.id == session?.user?.id));
    setUsersEvents(sorted.filter((ue) => ue.status !== "declined"));
  }, [props.event, session]);

  useEffect(() => {
    if (router?.query?.open_rating) {
      setShowRateSheet(true);
      delete router.query.open_rating;
      router.replace(router);
    }
  }, [router]);

  const approveUser = async (users_event) => {
    setUsersEvent(null);
    approveUsersEvent(users_event.id).then((response) => {
      if (response.status === "approved") {
        props.refetch();

        Toast.fire({
          icon: "success",
          title: t("enums:users_event_status.approved"),
        });
      }
    });
  };

  const removeUserFromGroup = (users_event) => {
    setUsersEvent(null);

    declineUsersEvent(users_event.id).then((response) => {
      props.refetch();
      Toast.fire({
        icon: "success",
        title: t("events.removed", { name: users_event.user.first_name }),
      });
    });
  };

  const requestUnite = () => {
    push(["trackGoal", 7]);
    joinUserToEvent(props.event.slug, session.user.id)
      .then((response) => {
        if (props.refetch) props.refetch();

        Swal.fire({
          icon: "success",
          title: t("common:events.detail.congrats"),
          customClass: {
            confirmButton: "btn btn-primary",
          },
          buttonsStyling: false,
          text: props.event.approve_participants
            ? t("common:events.detail.requested")
            : t("common:events.detail.joined"),
        });
      })
      .catch((error) => {
        Swal.fire({
          icon: "error",
          title: t("common:wrong"),
          text: error.data?.errors?.join(", "),
          customClass: {
            confirmButton: "btn btn-primary",
          },
          buttonsStyling: false,
        });
      });
  };

  const leaveEvent = () => {
    setShowMenuSheet(false);

    Swal.fire({
      title: t("events.detail.leave"),
      showDenyButton: true,
      showCancelButton: false,
      confirmButtonText: t("events.detail.opt_out"),
      denyButtonText: t("cancel"),
      customClass: {
        confirmButton: "btn btn-danger text-white me-2",
        denyButton: "btn btn-primary",
      },
      buttonsStyling: false,
    }).then((result) => {
      if (result.isConfirmed) {
        deleteUserFromEvent(currentUserEvent.id).then((response) => {
          if (props.refetch) props.refetch();
        });
      }
    });
  };

  const canRequestUnite = () => {
    return (
      !currentUserEvent &&
      dayjs().isBefore(dayjs(props.event.starts_at)) &&
      usersEvents.length < props.event.max_participants
    );
  };

  const canCancelRequest = () => {
    return (
      currentUserEvent &&
      currentUserEvent.user.id !== props.event.user_id &&
      dayjs().isBefore(dayjs(props.event.starts_at))
    );
  };

  const goToChat = () => {
    getEventChatRoom(props.event.slug)
      .then((data) => {
        router.push(`/chat_rooms/${data.id}`);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const canRateEvent = () => {
    return currentUserEvent?.status === "approved" && pastEvent(props.event);
  };

  return (
    <>
      <div
        className={`header bg ${
          props.event.cover_image?.url ? "" : `bg-${props.event.activity_group?.identifier?.toLowerCase()}`
        } cover-image`}
        style={{ backgroundImage: props.event.cover_image?.url ? `url(${props.event.cover_image.url})` : "" }}
      >
        <Navbar
          left={
            <li className="nav-item">
              <a className="nav-link navbar-nav float-start cover-image-btn p-1" onClick={() => goBack(router, window)}>
                <XLg size={ICON_SIZE} />
              </a>
            </li>
          }
          title=""
          fixed={false}
          right={
            <li className="nav-item">
              <a className="nav-link navbar-nav float-end cover-image-btn p-1" onClick={() => setShowMenuSheet(true)}>
                <ThreeDots size={ICON_SIZE} />
              </a>
            </li>
          }
        />
        <div className="container">
          <div className="header-images">
            {props.event.admin_group ? (
              <Link href={`/groups/${props.event.admin_group.token}`}>
                <a>
                  <UserImage
                    src={props.event.admin_group.logo_thumb}
                    name={props.event.admin_group.name}
                    width={48}
                    height={48}
                  />
                </a>
              </Link>
            ) : (
              <Link href={`/profile/${props.event.user.id}`}>
                <a>
                  <UserImage
                    src={props.event.user.avatar_thumb}
                    name={props.event.user.first_name}
                    width={32}
                    height={32}
                  />
                </a>
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="container-sm min-100 position-relative mt-36 mb-55">
        <h2>{props.event.name}</h2>

        {props.event.published_user_groups && props.event.published_user_groups.length > 0 && (
          <IconsList groups={props.event.published_user_groups} marginClass="me-1" />
        )}

        <div className="mt-3">
          <Badges event={props.event} t={t} />
        </div>

        <div className="mt-4 mb-24">
          <small className="text-uppercase text-gray1 label-2 d-flex align-items-center mb-1">
            {t("common:details")}
          </small>
          <span className="font-size-md">
            {dayjs(props.event.starts_at).format("DD.MM.YYYY")} <span className="text-gray1">@</span>{" "}
            {dayjs(props.event.starts_at).format("HH:mm")} - {dayjs(props.event.ends_at).format("HH:mm")}
          </span>
          {props.event.flexible_start && (
            <div className="d-flex mt-2">
              <div className="col">
                <span className="text-uppercase mb-2 text-gray1 label-2 mb-1">
                  {t("common:events.detail.flexible_start")}
                </span>
                <div className="col-auto">{props.event.flexible_text}</div>
              </div>
            </div>
          )}

          {props.event.approve_participants && (
            <div className="mt-1">
              <small className="text-gray1">
                {t("common:events.detail.approve_participants", {
                  // time: `${props.event.latest_approval_val}${props.event.latest_approval_unit[0]}`,
                  time: "",
                })}
                🔒
              </small>
            </div>
          )}
        </div>

        <div className="mb-24">
          <div className="d-flex">
            <div className="col">
              <small className="label-2 text-uppercase text-gray1 d-flex align-items-center mb-1">
                {t("common:address")}
              </small>
              <a onClick={() => (currentUserEvent?.status === "approved" ? setShowMapSheet(true) : {})}>
                {currentUserEvent?.status === "approved" ? props.event.full_address : props.event.city}
              </a>
            </div>
            <div className="col-auto align-content-end label-2 text-uppercase">
              <a className="float-end" onClick={() => copyAddress()}>
                {t("copy")}
              </a>
            </div>
          </div>
        </div>

        {props.event.description && (
          <div className="mb-2 pre-line">
            <small className="label-2 text-uppercase text-gray1 d-flex align-items-center mb-1">
              {t("common:description")}
            </small>
            <div dangerouslySetInnerHTML={{ __html: highlightLinksInText(props.event.description) }} />
          </div>
        )}

        <div className="mb-24 mt-2">
          <div className="d-flex align-items-center mb-12">
            <div className="col">
              <small className="label-2 text-uppercase text-gray1">{t("members")}</small>
            </div>
            <div className="col-auto align-content-end label-2">
              {canRateEvent() && (
                <a className="float-end text-uppercase" onClick={() => setShowRateSheet(true)}>
                  {`${t("events.detail.rate_members")}`}
                </a>
              )}

              {!pastEvent(props.event) && (
                <a className="float-end text-uppercase" onClick={() => setShowInviteSheet(true)}>
                  {t("invite")}
                </a>
              )}
            </div>
          </div>

          <AvatarList
            users={approvedUsers.map((ue) => ue.user)}
            showMoreLink={membersLink}
            totalCount={props.event.users_events_count}
            membersListLink={`/events/${props.event.slug}/members`}
          />

          {pastEvent(props.event) && currentUserEvent?.status === "approved" && (
            <BlockGap>
              <Link href={`/events/new?slug=${props.event.slug}`}>
                <a className="btn btn-primary btn-primary-lg align-items-center justify-content-center mt-3">
                  {t("events.unite_again")}
                </a>
              </Link>
            </BlockGap>
          )}

          <div className="mt-4">
            {currentUserEvent?.status === "approved" && (
              <BlockGap>
                <a
                  className={`d-flex align-items-center justify-content-center btn btn-lg mt-2 mb-3 ${
                    pastEvent(props.event) ? "btn-outline-primary" : "btn-primary"
                  }`}
                  onClick={goToChat}
                >
                  <ImageIcon name={pastEvent(props.event) ? "chat" : "chats-white"} raw={true} />
                  <span className="ms-1">{t("open_chat")}</span>
                </a>
              </BlockGap>
            )}

            {canRequestUnite() && usersEvents.length < props.event.max_participants && (
              <BlockGap>
                <button className="btn btn-primary btn-lg mt-3 mb-3" onClick={requestUnite}>
                  {props.event.approve_participants ? t("events.detail.request") : t("events.detail.join")}
                </button>
              </BlockGap>
            )}

            {canCancelRequest() &&
              currentUserEvent?.status !== "declined" &&
              currentUserEvent?.status !== "invited_by_creator" && (
                <BlockGap>
                  <button className="btn btn-outline-primary btn-lg mt-3 mb-3" onClick={leaveEvent}>
                    {currentUserEvent?.status === "approved"
                      ? t("events.detail.opt_out")
                      : t("events.detail.cancel_request")}
                  </button>
                </BlockGap>
              )}

            {!pastEvent(props.event) && currentUserEvent?.status === "invited_by_creator" && (
              <BlockGap>
                <button className="btn btn-primary btn-lg mt-3" onClick={() => approveUser(currentUserEvent)}>
                  {t("approve")}
                </button>

                <button
                  className="btn btn-outline-primary btn-lg mt-1 mb-3"
                  onClick={() => removeUserFromGroup(currentUserEvent)}
                >
                  {t("decline")}
                </button>
              </BlockGap>
            )}

            {currentUserEvent?.status === "declined" && (
              <BlockGap>
                <h2 className="text-center text-danger">⛔ {t("events.detail.declined")}</h2>
                <button className="btn btn-primary btn-lg mt-3 mb-3" onClick={() => router.replace("/search")}>
                  <ImageIcon name="search-white" raw={true} />
                  <span className="ms-1">{t("events.detail.search_another")}</span>
                </button>
              </BlockGap>
            )}
          </div>
        </div>
      </div>

      <Suspense>
        <MenuSheet
          isOpen={showMenuSheet}
          onClose={() => setShowMenuSheet(false)}
          canCancelRequest={canCancelRequest}
          leaveEvent={leaveEvent}
          event={props.event}
          onShareClick={() => {
            setShowMenuSheet(false);
            setShowInviteSheet(true);
          }}
        />

        <ApproveSheet
          isOpen={usersEvent && true}
          onClose={() => setUsersEvent(null)}
          event={props.event}
          usersEvent={usersEvent}
          approveUser={() => approveUser(usersEvent)}
          declineUser={() => removeUserFromGroup(usersEvent)}
        />

        <RatingSheet
          isOpen={showRateSheet}
          onClose={() => setShowRateSheet(false)}
          currentUserEvent={currentUserEvent}
          event={props.event}
          approvedUsers={approvedUsers}
        />

        <InviteSheet
          name={props.event.name}
          slug={props.event.slug}
          url={eventURL}
          queryCacheKey="usersForInvite"
          getUsersForInvite={getEventUsersForInvite}
          inviteUserClick={(user) => inviteUserToEvent(props.event, user, t, props.refetch)}
          isOpen={showInviteSheet}
          onClose={() => setShowInviteSheet(false)}
          showUsers={canAddMembers(props.event, approvedUsers) && !pastEvent(props.event)}
          title={pastEvent(props.event) ? t("share") : null}
        />

        {props.event.lat && props.event.lng && currentUserEvent?.status === "approved" && (
          <MapSheet
            isOpen={showMapSheet}
            onClose={() => setShowMapSheet(false)}
            lat={props.event.lat}
            lng={props.event.lng}
            position={{ lat: props.event.lat, lng: props.event.lng }}
            onSubmit={() => {}}
            showSearch={false}
            showSubmitButton={false}
            allowChangePosition={false}
            title={t("common:address")}
            goToUserPosition={false}
          />
        )}
      </Suspense>
      <DummyMargin size={2} />
      <NavbarBottom />
    </>
  );
};

export default Detail;
